import { Component, Input, OnInit } from "@angular/core";
import { ProductSale } from "../../../interfaces/tickets/ProductSale";
import { NgTicketsService } from "../../../services/tickets.service";
import { Ticket } from "../../../interfaces/tickets/Ticket";
import { Coupon } from "../../../interfaces/tickets/Coupon";
import { Customer, Establishment } from "../../../interfaces";
import { NgPaymentService } from "../../../services/payment.service";
import { UserService } from "../../../services/user.service";

@Component({
  selector: "ng-tickets-container",
  styleUrls: ["./container.component.scss", "../../tickets/styles/ticket.scss"],
  providers: [NgTicketsService],
  template: `
    <div *ngIf="!error">
      <ng-tickets-product-list
        *ngIf="currentStep == 'product-list'"
        [loading]="loading"
        [productSale]="productSale"
        [productList]="productList"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-product-list>

      <ng-tickets-card-selection
        *ngIf="currentStep == 'card-selection'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-card-selection>

      <ng-tickets-cards-list
        *ngIf="currentStep == 'card-list'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-cards-list>

      <ng-tickets-address-list
        *ngIf="currentStep == 'address-list'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-address-list>

      <ng-tickets-send-data
        *ngIf="currentStep == 'send-data'"
        [productSale]="productSale"
        [groupType]="establishment.group"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-send-data>

      <ng-tickets-add-card
        *ngIf="currentStep == 'add-card'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-add-card>

      <ng-tickets-add-address
        *ngIf="currentStep == 'add-address'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-add-address>

      <ng-tickets-card-confirm
        *ngIf="currentStep == 'card-confirm'"
        [productSale]="productSale"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-card-confirm>

      <ng-tickets-confirm-cvv
        *ngIf="currentStep == 'confirm-cvv'"
        [card]="productSale?.card"
        (nextStep)="nextStep($event)"
      >
      </ng-tickets-confirm-cvv>

      <ng-tickets-success
        *ngIf="currentStep == 'success'"
        [coupons]="coupons"
        [groupType]="establishment.group"
        [productSale]="productSale"
        (nextStep)="resetProductSaleState()"
      >
      </ng-tickets-success>

      <div class="loading-container" *ngIf="currentStep === 'checkout'">
        <i class="fa fa-spinner fa-2x fa-spin"></i>
        <h3>Processando pagamento</h3>
      </div>
    </div>

    <ng-tickets-error
      *ngIf="error"
      [errorTitle]="error?.title"
      [errorMessage]="error?.message"
      [backButton]="error?.backButton"
      (backButtonEvent)="nextStep('card-selection'); error = null"
    >
    </ng-tickets-error>
  `,
})
export class ContainerComponent implements OnInit {
  @Input()
  establishment: Establishment;

  productList: Ticket[] = [];
  currentStep = "product-list";
  loggedUser: Customer;
  loading = true;
  error: { title?: string; message: string; backButton?: boolean } = null;
  coupons: Coupon[];

  productSale: ProductSale = {
    customer_id: null,
    ticketProduct: null,
    ticketAmount: 1,
    card: null,
    billing_address: null,
    user: { id: null, name: null, email: null, phone: null },
    cardsLength: 0,
    addressLength: 0,
    saveCard: true,
  };

  constructor(
    private ticketsService: NgTicketsService,
    private userService: UserService,
    private paymentService: NgPaymentService
  ) {}

  ngOnInit(): void {
    this.getLoggedUser();
    this.getProducts();
  }

  nextStep(step: any): void {
    this.currentStep = step;

    if (step === "checkout") {
      this.checkout();
    }
  }

  getLoggedUser() {
    this.loggedUser = this.userService.getLoggedUser();
    this.productSale.user.id = this.loggedUser.id || null;
    this.productSale.user.name = this.loggedUser.name || null;
    this.productSale.user.email = this.loggedUser.email || null;
    this.productSale.user.phone = this.loggedUser.phone || null;
  }

  getProducts(): void {
    this.error = null;

    this.ticketsService.getAvailableProducts(this.establishment.id).subscribe(
      (products) => {
        this.productList = products;
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.error = {
          title: "Ocorreu um erro :(",
          message:
            "Ocorreu um erro ao tentar carregar os produtos! Por favor, recarregue a" +
            " página e tente novamente!",
        };
      }
    );
  }

  checkout() {
    if (
      this.productSale.card.id &&
      this.productSale.card.id !== "not-addable"
    ) {
      this.sendPayment();
    } else {
      this.sendSinglePayment();
    }
  }

  sendPayment() {
    const { cardsLength, addressLength, card, ...sale } = this.productSale;

    this.paymentService
      .sendPayment({ ...sale, card_id: card.id, card_vcc: card.cvv })
      .subscribe(
        (res) => {
          this.coupons = res.coupons;
          this.currentStep = "success";
        },
        (err) => {
          this.handleError(err);
        }
      );
  }

  sendSinglePayment() {
    const { card, ticketAmount, ticketProduct, user } = this.productSale;

    const paymentData = {
      cardNumber: card.card_number,
      cardCvv: card.cvv,
      cardHolderName: card.holder_name,
      cardExpireMonth: String(card.exp_month),
      cardExpireYear: String(card.exp_year),
      couponQuantity: ticketAmount,
      ticketProductId: ticketProduct.id,
      userEmail: user.email,
    };

    this.paymentService.sendSinglePayment(paymentData).subscribe(
      (res) => {
        this.coupons = res.coupons;
        this.currentStep = "success";
      },
      (err) => {
        this.handleError(err);
      }
    );
  }

  resetProductSaleState() {
    const user = {
      id: this.loggedUser.id || null,
      name: this.loggedUser.name || null,
      email: this.loggedUser.email || null,
      phone: this.loggedUser.phone || null,
    };

    this.productSale = {
      customer_id: null,
      ticketProduct: null,
      ticketAmount: 1,
      card: null,
      billing_address: null,
      user,
      cardsLength: 0,
      addressLength: 0,
      saveCard: true,
    };

    this.coupons = [];
    this.error = null;
    this.currentStep = "product-list";
  }

  handleError(res) {
    switch (res.error && res.error.status) {
      case "INSUFICIENT_TICKET": {
        this.error = {
          title: "Saldo de ingressos insuficiente!",
          message: "Infelizmente os ingressos disponíveis esgotaram.",
          backButton: true,
        };
        break;
      }
      case "PAYMENT_FAILED": {
        this.error = {
          title: "Pagamento recusado :(",
          message:
            "Houve um problema ao processar seu pedido. Entre em contato com a operadora do cartão ou tente novamente.",
          backButton: true,
        };
        break;
      }
      case "SERVICE_UNAVAILABLE": {
        this.error = {
          title: "Serviço indisponível :(",
          message:
            "Serviço indisponível no momento, entre em contato com o suporte ou tente novamente mais tarde.",
          backButton: true,
        };
        break;
      }
      default: {
        this.error = {
          title: "Desculpa, ocorreu um erro :(",
          message:
            "Ocorreu um erro ao realizar a compra. Por favor, entre em contato com o suporte ou tente novamente mais tarde.",
          backButton: true,
        };
      }
    }
  }
}
